import type { FileTreeNode } from "./types";

interface MutableTreeNode {
  type: "file" | "folder";
  name: string;
  path: string;
  children?: Map<string, MutableTreeNode>;
}

const sortNodes = (a: MutableTreeNode, b: MutableTreeNode) => {
  if (a.type !== b.type) {
    return a.type === "folder" ? -1 : 1;
  }

  return a.name.localeCompare(b.name);
};

const ensureFolderNode = (root: Map<string, MutableTreeNode>, folderPath: string) => {
  const parts = folderPath.split("/").filter(Boolean);
  let current = root;

  parts.forEach((part, index) => {
    const currentPath = parts.slice(0, index + 1).join("/");
    const existing = current.get(part);
    if (existing?.type === "folder" && existing.children) {
      current = existing.children;
      return;
    }

    const nextNode: MutableTreeNode = {
      type: "folder",
      name: part,
      path: currentPath,
      children: new Map<string, MutableTreeNode>(),
    };
    current.set(part, nextNode);
    current = nextNode.children!;
  });
};

const addFileNode = (root: Map<string, MutableTreeNode>, filePath: string) => {
  const parts = filePath.split("/").filter(Boolean);
  const fileName = parts[parts.length - 1];
  if (!fileName) {
    return;
  }

  const parentPath = parts.slice(0, -1).join("/");
  if (parentPath) {
    ensureFolderNode(root, parentPath);
  }

  let current = root;
  parts.slice(0, -1).forEach(part => {
    const folder = current.get(part);
    if (folder?.type === "folder" && folder.children) {
      current = folder.children;
    }
  });

  current.set(fileName, {
    type: "file",
    name: fileName,
    path: filePath,
  });
};

const mapMutableRootToTree = (nodes: Map<string, MutableTreeNode>): FileTreeNode[] =>
  Array.from(nodes.values())
    .sort(sortNodes)
    .map(node => {
      if (node.type === "file") {
        return {
          type: "file",
          name: node.name,
          path: node.path,
        };
      }

      return {
        type: "folder",
        name: node.name,
        path: node.path,
        children: mapMutableRootToTree(node.children ?? new Map<string, MutableTreeNode>()),
      };
    });

export const buildTreeFromPaths = (paths: string[]): FileTreeNode[] => {
  const root = new Map<string, MutableTreeNode>();
  const folderPaths = paths.filter(path => path.endsWith("/")).map(path => path.replace(/\/$/, ""));
  const filePaths = paths.filter(path => !path.endsWith("/"));

  folderPaths
    .sort((a, b) => a.split("/").length - b.split("/").length)
    .forEach(folderPath => ensureFolderNode(root, folderPath));

  filePaths.sort().forEach(filePath => addFileNode(root, filePath));

  return mapMutableRootToTree(root);
};

export const getFirstFilePath = (paths: string[]): string | null => {
  const walk = (nodes: FileTreeNode[]): string | null => {
    for (const node of nodes) {
      if (node.type === "file") {
        return node.path;
      }

      const nested = walk(node.children ?? []);
      if (nested) {
        return nested;
      }
    }

    return null;
  };

  return walk(buildTreeFromPaths(paths));
};

export const collectExpandedFoldersForPath = (path: string) => {
  const nextFolders = new Set<string>();
  const parts = path.split("/").slice(0, -1);

  parts.forEach((_, index) => {
    nextFolders.add(parts.slice(0, index + 1).join("/"));
  });

  return nextFolders;
};
